/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, April 8th 2022, 6:06:50 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { PrimaryKeyValue } from "lib/types";
import { useAPIFetchPreBuiltServiceCategories } from "prebuilt-services/services/PreBuiltServicesAPI";
import { ProcessTemplateCategory } from "lib/schemas";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";

/**Lazy options selector */
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

export const PreBuiltServiceCategorySelector = ({
  selectedCategoryId,
  onSelectItem = null,
  showAllSelector = true,
  onSelectAll = null,
}) => {
  const {
    prebuilt_service_categories,
    isMakingAPIRequest,
    isAPIError,
    fetchCategoriesStatusCode,
    handleSelectAll,
    handleSelectItem,
  } = usePreBuiltServiceCategorySelector(onSelectItem, onSelectAll);

  return (
    <APIListProgressWrapper
      items={prebuilt_service_categories}
      showLoader={isMakingAPIRequest}
      showMessageWhenEmpty={false}
      isAPIError={isAPIError}
      httpResponseStatusCode={fetchCategoriesStatusCode}
      loaderType="linear"
    >
      <Stack direction="row" spacing={1}>
        {showAllSelector && (
          <Chip
            label={"All"}
            onClick={handleSelectAll}
            variant={!selectedCategoryId ? "filled" : "outlined"}
            color={!selectedCategoryId ? "primary" : "default"}
          />
        )}

        {prebuilt_service_categories?.map((category) => (
          <Chip
            label={category.title}
            onClick={() => handleSelectItem(category)}
            variant={selectedCategoryId === category.id ? "filled" : "outlined"}
            color={selectedCategoryId === category.id ? "primary" : "default"}
            key={`${category.id}`}
          />
        ))}
      </Stack>
    </APIListProgressWrapper>
  );
};

const usePreBuiltServiceCategorySelector = (onSelectItem, onSelectAll) => {
  const {
    isMakingAPIRequest,
    prebuilt_service_categories,
    isAPIError,
    httpResponseStatusCode: fetchCategoriesStatusCode,
  } = useAPIFetchPreBuiltServiceCategories();

  const handleSelectItem = (category: ProcessTemplateCategory) => {
    onSelectItem?.(category);
  };

  const handleSelectAll = () => {
    onSelectAll?.();
  };

  return {
    isMakingAPIRequest,
    prebuilt_service_categories,
    isAPIError,
    fetchCategoriesStatusCode,
    handleSelectItem,
    handleSelectAll,
  };
};

export const useCategorySelectionState = () => {
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<PrimaryKeyValue<ProcessTemplateCategory>>();

  const onSelectAllCategories = () => {
    setSelectedCategoryId(null);
  };

  const onSelectCategory = (category: ProcessTemplateCategory) => {
    setSelectedCategoryId(category.id);
  };

  return { selectedCategoryId, onSelectAllCategories, onSelectCategory };
};
